import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export type UserRole = "user" | "host" | "admin" | "affiliate" | "super_admin";

interface UserRolesResult {
  roles: UserRole[];
  isHost: boolean;
  isGuest: boolean;
  isAffiliate: boolean;
  isSuperAdmin: boolean;
  isAdmin: boolean;
  loading: boolean;
}

export function useUserRoles(): UserRolesResult {
  const { user } = useAuth();

  const { data: roles = [], isLoading } = useQuery({
    queryKey: ["user-roles", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id);
      if (error) return [];
      return data.map((r) => r.role as UserRole);
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5,
  });

  const isSuperAdmin = roles.includes("super_admin");
  const isAdmin = roles.includes("admin");

  return {
    roles,
    isHost: roles.includes("host") || isSuperAdmin,
    isGuest: roles.includes("user") && !roles.includes("host") && !isSuperAdmin,
    isAffiliate: roles.includes("affiliate"),
    isSuperAdmin,
    isAdmin: isAdmin || isSuperAdmin,
    loading: isLoading,
  };
}
